import { useState, useMemo, useCallback } from 'react';

const SORT_METHODS = [
  { name: 'Product Name: A-Z', field: 'title', reverse: false },
  { name: 'Product Name: Z-A', field: 'title', reverse: true },
  { name: 'Price: Low-to-High', field: 'SuggRetailPrice', reverse: false },
  { name: 'Price: High-to-Low', field: 'SuggRetailPrice', reverse: true },
  { name: 'Distance', field: 'Distance', reverse: false },
];

export { SORT_METHODS };

const CATEGORY_DIMENSION = 'Category';
const STORE_DIMENSION = 'Store';

function getCategoryTitle(item) {
  return item.SubCategoryDesc ?? 'Other';
}

export function useUsedProductFilter(items) {
  const [keyword, setKeyword] = useState('');
  const [categories, setCategories] = useState([]);
  const [sortMethod, setSortMethod] = useState(SORT_METHODS[0]);
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);

  const initCategories = useCallback((sourceItems) => {
    setCategories((prev) => {
      const selectionMap = new Map(prev.map((category) => [category.title, category.selected]));
      const countMap = {};
      sourceItems.forEach((item) => {
        const cat = getCategoryTitle(item);
        countMap[cat] = (countMap[cat] ?? 0) + 1;
      });
      return Object.entries(countMap)
        .sort(([categoryA], [categoryB]) => categoryA.localeCompare(categoryB))
        .map(([title, count]) => ({
          title,
          count,
          selected: selectionMap.get(title) ?? false,
        }));
    });
  }, []);

  const toggleCategory = useCallback((title) => {
    setCategories((prev) => prev.map((cat) => (cat.title === title ? { ...cat, selected: !cat.selected } : cat)));
  }, []);

  const toggleStore = useCallback((storeId) => {
    setSelectedStoreIds((prev) => (
      prev.includes(storeId) ? prev.filter((id) => id !== storeId) : [...prev, storeId]
    ));
  }, []);

  const storeFilteredItems = useMemo(() => {
    if (selectedStoreIds.length === 0) return items;
    return items.filter((item) => selectedStoreIds.includes(item.StrNbr));
  }, [items, selectedStoreIds]);

  const categoriesWithLiveCounts = useMemo(() => {
    const countMap = {};
    storeFilteredItems.forEach((item) => {
      const cat = getCategoryTitle(item);
      countMap[cat] = (countMap[cat] ?? 0) + 1;
    });
    return categories.map((cat) => ({
      ...cat,
      count: countMap[cat.title] ?? 0,
    }));
  }, [categories, storeFilteredItems]);

  const filteredItems = useMemo(() => {
    const selectedCats = categoriesWithLiveCounts
      .filter((category) => category.selected)
      .map((category) => category.title);
    const kw = keyword.trim().toLowerCase();
    let result = storeFilteredItems;

    if (selectedCats.length > 0) {
      result = result.filter((item) => selectedCats.includes(getCategoryTitle(item)));
    }

    if (kw.length >= 2) {
      result = result.filter((item) => (item.title ?? '').toLowerCase().includes(kw));
    }

    return [...result].sort((leftItem, rightItem) => {
      const leftVal = leftItem[sortMethod.field] ?? '';
      const rightVal = rightItem[sortMethod.field] ?? '';
      if (sortMethod.field === 'SuggRetailPrice' || sortMethod.field === 'Distance') {
        const diff = Number.parseFloat(leftVal) - Number.parseFloat(rightVal);
        return sortMethod.reverse ? -diff : diff;
      }
      const cmp = String(leftVal).localeCompare(String(rightVal));
      return sortMethod.reverse ? -cmp : cmp;
    });
  }, [storeFilteredItems, categoriesWithLiveCounts, keyword, sortMethod]);

  const dimensions = useMemo(() => ([
    {
      label: CATEGORY_DIMENSION,
      dimensionName: CATEGORY_DIMENSION,
      refinements: categoriesWithLiveCounts.map((cat) => ({
        label: cat.title,
        refinementKey: `category:${cat.title}`,
        recordCount: String(cat.count ?? 0),
        selected: !!cat.selected,
      })),
    },
    {
      label: STORE_DIMENSION,
      dimensionName: STORE_DIMENSION,
      refinements: selectedStoreIds.map((storeId) => ({
        label: storeId,
        refinementKey: `store:${storeId}`,
        selected: true,
      })),
    },
  ]), [categoriesWithLiveCounts, selectedStoreIds]);

  const appliedDimensions = useMemo(() => {
    const categoryRefinements = categoriesWithLiveCounts
      .filter((cat) => cat.selected)
      .map((cat) => ({
        label: cat.title,
        refinementKey: `category:${cat.title}`,
      }));

    const storeRefinements = selectedStoreIds.map((storeId) => ({
      label: storeId,
      refinementKey: `store:${storeId}`,
    }));

    const applied = [];
    if (categoryRefinements.length > 0) {
      applied.push({ label: CATEGORY_DIMENSION, refinements: categoryRefinements });
    }
    if (storeRefinements.length > 0) {
      applied.push({ label: STORE_DIMENSION, refinements: storeRefinements });
    }
    return applied;
  }, [categoriesWithLiveCounts, selectedStoreIds]);

  const onDimensionsChange = useCallback(({ refinement, dimensionName }) => {
    const name = (dimensionName || '').toUpperCase();
    if (name === CATEGORY_DIMENSION.toUpperCase()) {
      toggleCategory(refinement?.label);
      return;
    }
    if (name === STORE_DIMENSION.toUpperCase()) {
      toggleStore(refinement?.label);
    }
  }, [toggleCategory, toggleStore]);

  const clearAllRefinements = useCallback(() => {
    setSelectedStoreIds([]);
    setCategories((prev) => prev.map((cat) => ({ ...cat, selected: false })));
    setKeyword('');
  }, []);

  return {
    filteredItems,
    keyword,
    categories: categoriesWithLiveCounts,
    dimensions,
    appliedDimensions,
    sortMethod,
    selectedStoreIds,
    setKeyword,
    toggleCategory,
    toggleStore,
    onDimensionsChange,
    clearAllRefinements,
    clearStoreFilter: () => setSelectedStoreIds([]),
    setSortMethod,
    initCategories,
    sortMethods: SORT_METHODS,
  };
}
